import React, { useEffect, useState, useMemo } from 'react';
import { Card, Typography, Spin, Alert, Button, Switch, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import { ApiService } from '@/services/api';
import { handleError } from '@/utils/ui-helpers';

const { Title, Text } = Typography;

// 类型颜色配置
const TYPE_COLORS: Record<string, string> = {
  InMeet: '#3b82f6',           // 蓝色
  OnlineMeet: '#10b981',       // 绿色
  unset: '#f59e0b',            // 橙色
  recordingDuration: '#ef4444', // 红色
};

// 类型中文名称
const TYPE_LABELS: Record<string, string> = {
  InMeet: '实时会议',
  OnlineMeet: '在线会议',
  unset: '未分类',
  recordingDuration: '录音时长',
};

interface ChartDataItem {
  date: string;
  [key: string]: number | string;
}

const TechAnalysisPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawData, setRawData] = useState<API.SlsErrCountItem[]>([]);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getErrCountByType();
      if (response.data.success) {
        setRawData(response.data.data || []);
      } else {
        setError(response.data.message || '获取数据失败');
      }
    } catch (err) {
      handleError(err, '获取错误统计数据失败', 'tech-analysis');
      setError('获取数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 获取所有唯一的类型
  const types = useMemo(() => {
    const typeSet = new Set(rawData.map(item => item.type));
    return Array.from(typeSet);
  }, [rawData]);

  // 转换数据为图表格式
  const chartData = useMemo((): ChartDataItem[] => {
    // 按时间分组
    const dateMap = new Map<string, ChartDataItem>();
    
    rawData.forEach(item => {
      // 格式化日期 - 只保留日期部分
      const date = item.time.split(' ')[0];
      
      if (!dateMap.has(date)) {
        const newItem: ChartDataItem = { date };
        // 初始化所有类型为 0
        types.forEach(type => {
          newItem[type] = 0;
        });
        dateMap.set(date, newItem);
      }
      
      const existingItem = dateMap.get(date)!;
      existingItem[item.type] = (existingItem[item.type] as number) + item.cnt;
    });

    // 按日期排序
    return Array.from(dateMap.values()).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [rawData, types]);

  // 动态生成图表配置
  const chartConfig = useMemo((): ChartConfig => {
    const config: ChartConfig = {};
    types.forEach(type => {
      config[type] = {
        label: TYPE_LABELS[type] || type,
        color: TYPE_COLORS[type] || '#6b7280',
      };
    });
    return config;
  }, [types]);

  // 计算统计信息
  const statistics = useMemo(() => {
    const typeStats: Record<string, number> = {};
    let total = 0;
    
    rawData.forEach(item => {
      typeStats[item.type] = (typeStats[item.type] || 0) + item.cnt;
      total += item.cnt;
    });

    return { typeStats, total };
  }, [rawData]);

  // 渲染图表内容
  const renderChart = () => {
    if (chartType === 'bar') {
      return (
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(value) => {
                  return new Date(value).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  });
                }}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          {types.map(type => (
            <Bar
              key={type}
              dataKey={type}
              stackId="a"
              fill={TYPE_COLORS[type] || '#6b7280'}
            />
          ))}
        </BarChart>
      );
    }

    return (
      <LineChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => {
            const date = new Date(value);
            return `${date.getMonth() + 1}/${date.getDate()}`;
          }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(value) => {
                return new Date(value).toLocaleDateString('zh-CN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });
              }}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        {types.map(type => (
          <Line
            key={type}
            type="monotone"
            dataKey={type}
            stroke={TYPE_COLORS[type] || '#6b7280'}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <Title level={3} className="mb-2">
            技术分析
          </Title>
          <Text type="secondary">
            系统错误类型统计分析
          </Text>
        </div>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchData}
          loading={loading}
        >
          刷新数据
        </Button>
      </div>

      {error && (
        <Alert 
          message="错误" 
          description={error} 
          type="error" 
          className="mb-4"
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card size="small">
          <Text type="secondary">总错误数</Text>
          <div className="text-2xl font-bold text-gray-900">{statistics.total}</div>
        </Card>
        {types.map(type => (
          <Card size="small" key={type}>
            <Text type="secondary">{TYPE_LABELS[type] || type}</Text>
            <div 
              className="text-2xl font-bold" 
              style={{ color: TYPE_COLORS[type] || '#6b7280' }}
            >
              {statistics.typeStats[type] || 0}
            </div>
          </Card>
        ))}
      </div>

      {/* 图表 */}
      <Card 
        title="错误趋势图" 
        className="mb-4"
        extra={
          <Space>
            <Text type="secondary">折线图</Text>
            <Switch 
              checked={chartType === 'bar'}
              onChange={(checked) => setChartType(checked ? 'bar' : 'line')}
            />
            <Text type="secondary">堆叠柱形图</Text>
          </Space>
        }
      >
        {loading ? (
          <div className="flex justify-center items-center h-80">
            <Spin size="large" tip="加载中..." />
          </div>
        ) : chartData.length > 0 ? (
          <ChartContainer config={chartConfig} className="h-[400px] w-full">
            {renderChart()}
          </ChartContainer>
        ) : (
          <div className="flex justify-center items-center h-80 text-gray-500">
            暂无数据
          </div>
        )}
      </Card>
    </div>
  );
};

export default TechAnalysisPage;
